import { Suspense, useState, useEffect } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  BookOpen,
  ChevronDown,
  Dna,
  Files,
  Leaf,
  ListChecks,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Plus,
  Search,
  Sun,
  UserRound,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LoadingState } from "@/components/common";
import { AuthDialog } from "./AuthDialog";
import {
  SessionProvider,
  useSession,
  isActive,
  statusLabels,
} from "@/lib/runs";
import { api } from "@/lib/api";
import { useMobileViewport } from "@/lib/use-mobile-viewport";
import { toast } from "sonner";

const references = [
  { path: "/variants", label: "变异资料", icon: Dna },
  { path: "/publications", label: "文献库", icon: BookOpen },
  { path: "/gaps", label: "资料缺口", icon: Files },
  { path: "/standards", label: "指南与标准", icon: ListChecks },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [logoutBusy, setLogoutBusy] = useState(false);
  const isReference = references.some((entry) =>
    location.pathname.startsWith(entry.path),
  );
  const [referenceOpen, setReferenceOpen] = useState(isReference);
  useEffect(() => {
    if (isReference) setReferenceOpen(true);
  }, [isReference]);
  const filtered = session.runs.filter((run) =>
    run.title.toLowerCase().includes(search.toLowerCase()),
  );
  async function logout() {
    setLogoutBusy(true);
    try {
      await api("/auth/logout", { method: "POST" });
      for (const key of Object.keys(sessionStorage))
        if (key.startsWith("mh-chat-")) sessionStorage.removeItem(key);
      session.setUser(null);
      navigate("/");
      onNavigate?.();
    } catch {
      toast.error("退出未能完成，请重试。");
    } finally {
      setLogoutBusy(false);
    }
  }
  return (
    <>
      <div className="chat-sidebar-header">
        <Link
          className="chat-brand"
          to="/"
          onClick={onNavigate}
          aria-label="MH Evidence 首页"
        >
          <span className="brand-icon">
            <Leaf size={20} />
          </span>
          <span>
            <span className="brand-title">MH Evidence</span>
            <span className="brand-note block">恶性高热 · 变异判读</span>
          </span>
        </Link>
        {onNavigate && (
          <Button
            className="drawer-close"
            variant="ghost"
            size="icon"
            aria-label="关闭导航"
            onClick={onNavigate}
          >
            <X size={20} />
          </Button>
        )}
      </div>
      <div className="chat-sidebar-content">
        <Button className="new-conversation" variant="outline" asChild>
          <Link to="/" onClick={onNavigate}>
            <Plus size={17} />
            新建判读
            <span className="ml-auto text-muted-foreground text-xs">RYR1</span>
          </Link>
        </Button>
        <section className="chat-history" aria-label="历史对话">
          <div className="chat-sidebar-label">
            <span>最近对话</span>
            {session.runs.some((run) => isActive(run.status)) && (
              <span>
                {session.runs.filter((run) => isActive(run.status)).length}{" "}
                项进行中
              </span>
            )}
          </div>
          {session.runs.length > 6 && (
            <div className="chat-search">
              <Search size={14} />
              <Input
                aria-label="搜索最近对话"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="搜索对话"
              />
            </div>
          )}
          <nav className="chat-run-list">
            {session.runsLoading && (
              <p className="sidebar-empty">正在读取对话…</p>
            )}
            {session.runsError && (
              <div className="sidebar-empty">
                <p>对话暂时加载失败</p>
                <Button variant="ghost" size="sm" onClick={session.refreshRuns}>
                  重新加载
                </Button>
              </div>
            )}
            {!session.runsLoading &&
              !session.runsError &&
              filtered.length === 0 && (
                <p className="sidebar-empty">
                  {search
                    ? "没有找到匹配的对话"
                    : session.user
                      ? "从一个变异开始。你的判读会保存在这里。"
                      : "登录后，在这里继续之前的判读。"}
                </p>
              )}
            {filtered.map((run) => (
              <NavLink
                to={`/runs/${run.id}`}
                key={run.id}
                onClick={onNavigate}
                className={({ isActive: selected }) =>
                  `chat-run-link ${selected ? "selected" : ""}`
                }
                title={run.title}
              >
                <MessageSquare size={15} className="shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{run.title}</span>
                  <span className="chat-run-meta">
                    {run.cancel_requested && run.status !== "cancelled"
                      ? "正在取消"
                      : statusLabels[run.status]}
                  </span>
                </span>
                {isActive(run.status) && (
                  <span
                    className="run-dot"
                    aria-label={statusLabels[run.status]}
                  />
                )}
              </NavLink>
            ))}
          </nav>
        </section>
        <Collapsible
          open={referenceOpen}
          onOpenChange={setReferenceOpen}
          className="chat-reference-nav"
        >
          <CollapsibleTrigger
            className="chat-sidebar-label w-full"
            aria-label="展开或收起证据工作区"
          >
            <span className="flex gap-2 items-center">
              <BookOpen size={15} />
              证据工作区
            </span>
            <ChevronDown
              size={14}
              className={referenceOpen ? "rotate-180" : ""}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <nav className="grid gap-1 pt-2">
              {references.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={onNavigate}
                  className={({ isActive: selected }) =>
                    `chat-reference-link ${selected ? "selected" : ""}`
                  }
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}
            </nav>
          </CollapsibleContent>
        </Collapsible>
      </div>
      <div className="chat-account">
        {session.user ? (
          <>
            <span className="account-avatar">
              {session.user.display_name.slice(0, 1).toUpperCase()}
            </span>
            <span className="truncate flex-1" title={session.user.display_name}>
              {session.user.display_name}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="退出登录"
              title="退出登录"
              disabled={logoutBusy}
              onClick={logout}
            >
              <LogOut size={16} />
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={() => {
              onNavigate?.();
              session.openLogin();
            }}
            disabled={session.loading}
          >
            <UserRound size={17} />
            登录 / 创建账户
          </Button>
        )}
      </div>
    </>
  );
}

function Shell() {
  useMobileViewport();
  const session = useSession();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 768px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setMobileOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);
  const [dark, setDark] = useState(
    () => localStorage.getItem("mh-theme") === "dark",
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("mh-theme", dark ? "dark" : "light");
  }, [dark]);
  const isChat =
    location.pathname === "/" || location.pathname.startsWith("/runs/");
  const reference = references.find((entry) =>
    location.pathname.startsWith(entry.path),
  );
  const currentId = location.pathname.match(/^\/runs\/([^/]+)$/)?.[1];
  const current = session.runs.find((run) => run.id === currentId);
  return (
    <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
      <div className="agent-shell">
        <a href="#main" className="skip-link">
          跳到主要内容
        </a>
        <aside className="agent-sidebar">
          <Sidebar />
        </aside>
        <DialogContent
          placement="left"
          className="chat-mobile-drawer"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">对话与资料导航</DialogTitle>
          <DialogDescription className="sr-only">
            新建判读，打开历史对话或进入证据工作区。
          </DialogDescription>
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </DialogContent>
        <div className="agent-main">
          <header className="chat-topbar">
            <div className="flex items-center gap-3 min-w-0">
              <DialogTrigger asChild>
                <Button
                  className="mobile-menu"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="打开导航"
                >
                  <Menu size={19} />
                </Button>
              </DialogTrigger>
              <span className="truncate">
                {isChat
                  ? (current?.title ?? "RYR1 判读助手")
                  : (reference?.label ?? "证据工作区")}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {!session.user && (
                <Button
                  className="mobile-login"
                  variant="ghost"
                  size="sm"
                  onClick={session.openLogin}
                  disabled={session.loading}
                >
                  登录
                </Button>
              )}
              {current && (
                <span className="header-run-status">
                  <span
                    className={
                      isActive(current.status) ? "run-dot" : "status-static-dot"
                    }
                  />
                  {current.cancel_requested && isActive(current.status)
                    ? "正在取消"
                    : statusLabels[current.status]}
                </span>
              )}
              {!isChat && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/">
                    <MessageSquare size={16} />
                    <span className="return-chat-label">返回判读</span>
                  </Link>
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={dark ? "切换浅色外观" : "切换深色外观"}
                title={dark ? "切换浅色外观" : "切换深色外观"}
                onClick={() => setDark(!dark)}
              >
                {dark ? <Sun size={17} /> : <Moon size={17} />}
              </Button>
            </div>
          </header>
          <main
            id="main"
            tabIndex={-1}
            className={isChat ? "chat-main" : "agent-knowledge-main"}
          >
            <Suspense fallback={<LoadingState />}>
              {!isChat && session.loading ? (
                <LoadingState />
              ) : !isChat && !session.user ? (
                <div className="empty-state">
                  <UserRound size={24} aria-hidden />
                  <h3>登录后查看证据工作区</h3>
                  <p>使用邮箱验证码登录，查看文献、证据和指南。</p>
                  <Button onClick={session.openLogin}>邮箱登录</Button>
                </div>
              ) : (
                <Outlet />
              )}
            </Suspense>
          </main>
        </div>
        <AuthDialog />
      </div>
    </Dialog>
  );
}
export function ChatShell() {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}
